import * as Config from './config.js';
// The proxy otherwise reports upward through `worldManagerCallbacks`. The spacetime layer stream is
// the one exception: it carries transferred ArrayBuffers straight to the renderer, and threading
// them through WorldManager would add a hop that owns nothing and can only copy or drop them.
import { EventBus, EVENTS } from '../services/EventBus.js';

const SEED_INVALIDATING_COMMANDS = new Set([
    'LOAD_STATE',
    'APPLY_BRUSH',
    'APPLY_SELECTIVE_BRUSH',
    'SHIFT_STATE',
]);

export class WorldProxy {
    constructor(worldIndex, initialSettings, worldManagerCallbacks) {
        this.worldIndex = worldIndex;
        this.worker = new Worker(new URL('./WorldWorker.js', import.meta.url), { type: 'module' });
        // Corpus provenance: the seed that produced this world's current reset lineage. Capture is
        // non-destructive, so this remains valid as the world evolves until the next reset.
        this.lastResetSeed = Number.isFinite(initialSettings.seed) ? Number(initialSettings.seed) : null;

        this.latestStateArray = null;
        this.latestRuleIndexArray = null;
        // Hover highlight now lives main-thread-only (no worker round-trip). Allocated eagerly and
        // zeroed so the renderer always has a valid per-cell buffer to bind; mutated by
        // setHoverState/clearHoverState. Mirrors latestGhostStateArray.
        this.latestHoverStateArray = new Uint8Array(Config.NUM_CELLS);
        this.latestGhostStateArray = null;
        // Renderer dirty flag: true means this world's FBO needs to be redrawn.
        // Set whenever the visual buffers change (state/hover via STATE_UPDATE, ghost
        // via setGhostState/clearGhostState); cleared by the renderer after the FBO draw.
        // Starts true so the first frame always draws.
        this.renderDirty = true;
        this.latestStats = {
            tick: 0,
            activeCount: 0,
            ratio: 0,
            binaryEntropy: 0,
            blockEntropy: 0,
            isEnabled: initialSettings.enabled,
            tps: 0,
            rulesetHex: initialSettings.rulesetHex || "0".repeat(32),
            ratioHistory: [],
            entropyHistory: [],
            hexBlockEntropyHistory: [],
            ruleUsage: new Uint32Array(128),
            isInCycle: false,
            cycleLength: 0,
            historyLength: 0,
            isScrubbing: false
        };
        this.isInitialized = false;
        this.onUpdate = worldManagerCallbacks.onUpdate;
        this.onInitialized = worldManagerCallbacks.onInitialized;
        this.MAX_HISTORY_SIZE = Config.STATS_HISTORY_SIZE || 100;

        
        this.lastTickCountForServerUpdate = 0; 
        this.tpsAggregator = {
            ticksCounted: 0,
            startTime: performance.now(),
            calculationWindowSeconds: 0.5 
        };


        this.worker.onmessage = (event) => this._handleWorkerMessage(event.data);

        const initialConfig = {
            GRID_ROWS: initialSettings.config.GRID_ROWS,
            GRID_COLS: initialSettings.config.GRID_COLS,
            NUM_CELLS: initialSettings.config.NUM_CELLS,
        };
        
        // The cell state buffer now lives inside Wasm linear memory (allocated by the worker), and
        // hover is main-thread-only, so only the ruleset buffer needs to be handed over at init.
        const initialRulesetBuffer = new Uint8Array(initialSettings.rulesetArray).buffer.slice(0);

        this.worker.postMessage({
            type: 'INIT',
            data: {
                worldIndex: this.worldIndex,
                config: initialConfig,
                initialRulesetBuffer: initialRulesetBuffer,
                initialState: initialSettings.initialState, // Add this
                initialIsEnabled: initialSettings.enabled,
                speed: initialSettings.speed,
                initialEntropySamplingEnabled: initialSettings.initialEntropySamplingEnabled,
                initialEntropySampleRate: initialSettings.initialEntropySampleRate,
                seed: initialSettings.seed,
            }
        }, [initialRulesetBuffer]);
    }

    _handleWorkerMessage(data) {
        switch (data.type) {
            case 'INIT_ACK': {
                this.isInitialized = true;
                this.tpsAggregator.startTime = performance.now(); 
                this.lastTickCountForServerUpdate = 0;
                this.latestStats.ratioHistory = [];
                this.latestStats.entropyHistory = [];
                this.latestStats.hexBlockEntropyHistory = [];
                this.onInitialized(this.worldIndex);
                break;
            }
            case 'STATE_UPDATE': {
                // Hand the buffers we're about to replace back to the worker (transferred) so it
                // can refill them on the next grid send instead of allocating two fresh NUM_CELLS
                // ArrayBuffers each time. The renderer and save path read latestStateArray /
                // latestRuleIndexArray only synchronously (within an rAF / a save call), never
                // across an event-loop turn, so once they're reassigned below the old backing
                // buffers are unreferenced and safe to detach. A detached buffer reports
                // byteLength 0, so the guard also skips anything already transferred away.
                const reclaimable = [];
                if (this.latestStateArray && this.latestStateArray.buffer.byteLength > 0) {
                    reclaimable.push(this.latestStateArray.buffer);
                }
                if (this.latestRuleIndexArray && this.latestRuleIndexArray.buffer.byteLength > 0) {
                    reclaimable.push(this.latestRuleIndexArray.buffer);
                }
                this.latestStateArray = new Uint8Array(data.stateBuffer);
                this.latestRuleIndexArray = new Uint8Array(data.ruleIndexBuffer);
                if (reclaimable.length > 0) {
                    this.worker.postMessage(
                        { type: 'RECLAIM_BUFFERS', data: { buffers: reclaimable } },
                        reclaimable
                    );
                }
                if (this.latestGhostStateArray) {
                    this.latestGhostStateArray.fill(0);
                }
                this.renderDirty = true;
                this.onUpdate(this.worldIndex, 'state');
                break;
            }
            // --- Spacetime volume stream (#40) ------------------------------
            // Only ever arrives while the spacetime view is open and this world is selected, so the
            // four cases below are dead weight in every other session. Forwarded straight to the
            // EventBus rather than stored: the volume lives in GPU memory inside the lazily loaded
            // projection, and holding a second copy here would double a cost the plan sizes in
            // tens of megabytes.
            case 'SPACETIME_LAYER': {
                EventBus.dispatch(EVENTS.SPACETIME_LAYER, {
                    worldIndex: this.worldIndex,
                    tick: data.tick,
                    layerBuffer: data.layerBuffer,
                    // The renderer returns the buffer here once it has been uploaded, so the worker
                    // refills it on the next tick instead of allocating one per tick.
                    reclaim: (buffer) => this._reclaimBuffer(buffer),
                });
                break;
            }
            case 'SPACETIME_BACKFILL': {
                EventBus.dispatch(EVENTS.SPACETIME_BACKFILL, {
                    worldIndex: this.worldIndex,
                    count: data.count,
                    numCells: data.numCells,
                    buildMs: data.buildMs,
                    layersBuffer: data.layersBuffer,
                });
                break;
            }
            case 'SPACETIME_RESET': {
                EventBus.dispatch(EVENTS.SPACETIME_RESET, { worldIndex: this.worldIndex });
                break;
            }
            case 'SPACETIME_TRUNCATE': {
                EventBus.dispatch(EVENTS.SPACETIME_TRUNCATE, {
                    worldIndex: this.worldIndex,
                    length: data.length,
                });
                break;
            }
            case 'STATS_UPDATE': {
                const currentTime = performance.now();
                
                const ticksSinceLastWorkerUpdate = data.tick - this.lastTickCountForServerUpdate;

                this.tpsAggregator.ticksCounted += ticksSinceLastWorkerUpdate;
                const elapsedAggregatorTimeSeconds = (currentTime - this.tpsAggregator.startTime) / 1000;

                let smoothedTPS = this.latestStats.tps; 

                if (elapsedAggregatorTimeSeconds >= this.tpsAggregator.calculationWindowSeconds) {
                    if (this.tpsAggregator.ticksCounted > 0 && elapsedAggregatorTimeSeconds > 0) {
                        smoothedTPS = parseFloat((this.tpsAggregator.ticksCounted / elapsedAggregatorTimeSeconds).toFixed(1));
                    } else if (elapsedAggregatorTimeSeconds > 0) { 
                        smoothedTPS = 0;
                    }
                    
                    this.tpsAggregator.ticksCounted = 0;
                    this.tpsAggregator.startTime = currentTime;
                }

                if (data.ruleUsageCounters) {
                    this.latestStats.ruleUsage = new Uint32Array(data.ruleUsageCounters);
                }

                if (data.isEnabled && data.ratio !== undefined) {
                    this.latestStats.ratioHistory.push(data.ratio);
                    if (this.latestStats.ratioHistory.length > this.MAX_HISTORY_SIZE) {
                        this.latestStats.ratioHistory.shift();
                    }
                }
                if (data.isEnabled && data.binaryEntropy !== undefined) {
                    this.latestStats.entropyHistory.push(data.binaryEntropy);
                    if (this.latestStats.entropyHistory.length > this.MAX_HISTORY_SIZE) {
                        this.latestStats.entropyHistory.shift();
                    }
                }
                if (data.isEnabled && data.blockEntropy !== undefined) {
                    this.latestStats.hexBlockEntropyHistory.push(data.blockEntropy);
                    if (this.latestStats.hexBlockEntropyHistory.length > this.MAX_HISTORY_SIZE) {
                        this.latestStats.hexBlockEntropyHistory.shift();
                    }
                }

                this.latestStats = {
                    ...this.latestStats, 
                    tick: data.tick,
                    activeCount: data.activeCount,
                    ratio: data.ratio,
                    binaryEntropy: data.binaryEntropy,
                    blockEntropy: data.blockEntropy,
                    isEnabled: data.isEnabled,
                    tps: smoothedTPS, 
                    rulesetHex: data.rulesetHex || this.latestStats.rulesetHex,
                    isInCycle: data.isInCycle,
                    cycleLength: data.cycleLength,
                    historyLength: data.historyLength ?? this.latestStats.historyLength,
                    isScrubbing: data.isScrubbing ?? false
                };

                this.lastTickCountForServerUpdate = data.tick; 

                this.onUpdate(this.worldIndex, 'stats');
                break;
            }
            case 'EVALUATION_RESULT': {
                // Auto-explore burst summary (Phase 2). Decode the transferred rule-usage delta into a
                // typed array and resolve any pending runEvaluation() promise. onEvaluationResult lets
                // WorldManager (Phase 4) observe results without polling.
                const ruleUsageDelta = data.ruleUsageDelta
                    ? new Uint32Array(data.ruleUsageDelta)
                    : new Uint32Array(128);
                const result = { ...data, ruleUsageDelta };
                if (this._pendingEvaluation) {
                    const resolve = this._pendingEvaluation;
                    this._pendingEvaluation = null;
                    resolve(result);
                }
                if (this.onEvaluationResult) {
                    this.onEvaluationResult(this.worldIndex, result);
                }
                break;
            }
            case 'CYCLE_FRAMES': {
                // Resolve a pending getCycleFrames() request with per-frame views (state stays
                // bit-packed — the consumer unpacks), or null when no cycle is locked in.
                if (this._pendingCycleFrames) {
                    const resolve = this._pendingCycleFrames;
                    this._pendingCycleFrames = null;
                    const frames = data.frames
                        ? data.frames.map((f) => ({
                            state: new Uint8Array(f.stateBuffer),
                            rules: new Uint8Array(f.rulesBuffer),
                        }))
                        : null;
                    resolve(frames);
                }
                break;
            }
            case 'RUN_RECORDING_PROGRESS': {
                if (this._runRecordingProgress) this._runRecordingProgress(data.frames);
                break;
            }
            case 'RUN_RECORDING_COMPLETE': {
                // Resolve the pending startRunRecording() with the banked trajectory (per-frame views;
                // state stays bit-packed), or a cancelled marker on a discontinuity/early-abort.
                if (this._pendingRunRecording) {
                    const resolve = this._pendingRunRecording;
                    this._pendingRunRecording = null;
                    this._runRecordingProgress = null;
                    if (data.cancelled) { resolve({ cancelled: true }); break; }
                    const map = (arr) => (arr || []).map((f) => ({
                        state: new Uint8Array(f.stateBuffer),
                        rules: new Uint8Array(f.rulesBuffer),
                    }));
                    resolve({
                        transient: map(data.transient),
                        cycle: map(data.cycle),
                        cycleLength: data.cycleLength || 0,
                        cappedWithoutCycle: !!data.cappedWithoutCycle,
                    });
                }
                break;
            }
            case 'TRAJECTORY_CAPTURE_RESULT': {
                if (this._pendingTrajectoryCapture) {
                    const { resolve } = this._pendingTrajectoryCapture;
                    this._pendingTrajectoryCapture = null;
                    resolve({
                        frames: (data.frames || []).map((buffer) => new Uint8Array(buffer)),
                        sourceTick: Number(data.sourceTick) || 0,
                    });
                }
                break;
            }
            case 'TRAJECTORY_SERIES_CAPTURE_RESULT': {
                if (this._pendingTrajectoryCapture) {
                    const { resolve } = this._pendingTrajectoryCapture;
                    this._pendingTrajectoryCapture = null;
                    resolve((data.slices || []).map((slice) => ({
                        frames: (slice.frames || []).map((buffer) => new Uint8Array(buffer)),
                        sourceTick: Number(slice.sourceTick) || 0,
                    })));
                }
                break;
            }
            case 'TRAJECTORY_CAPTURE_ERROR': {
                if (this._pendingTrajectoryCapture) {
                    const { reject } = this._pendingTrajectoryCapture;
                    this._pendingTrajectoryCapture = null;
                    reject(new Error(data.error || 'Trajectory capture failed.'));
                }
                break;
            }
        }
    }

    setGhostState(indices) {
        if (!this.latestGhostStateArray) {
            this.latestGhostStateArray = new Uint8Array(Config.NUM_CELLS);
        }
        this.latestGhostStateArray.fill(0);
        for (const index of indices) {
            if (index >= 0 && index < Config.NUM_CELLS) {
                this.latestGhostStateArray[index] = 1;
            }
        }
        this.renderDirty = true;
    }

    clearGhostState() {
        if (this.latestGhostStateArray) {
            this.latestGhostStateArray.fill(0);
        }
        this.renderDirty = true;
    }

    sendCommand(commandType, commandData, transferList = []) {
        // Reset-seed provenance is valid only while state changes come from simulation ticks.
        // Hand edits, imports, and shifts introduce inputs the seed cannot reproduce.
        if (SEED_INVALIDATING_COMMANDS.has(commandType)) this.lastResetSeed = null;
        if (!this.isInitialized && commandType !== 'INIT') {
            
            return;
        }
        if (transferList.length > 0) {
            this.worker.postMessage({ type: commandType, data: commandData }, transferList);
        } else {
            this.worker.postMessage({ type: commandType, data: commandData });
        }
    }

    startSimulation() { this.sendCommand('START_SIMULATION', {}); }
    stopSimulation() { this.sendCommand('STOP_SIMULATION', {}); }
    setSpeed(speed) { this.sendCommand('SET_SPEED_TARGET', { speed }); }
    setEnabled(enabled) {
        
        this.latestStats.isEnabled = enabled;
        this.sendCommand('SET_ENABLED', { enabled });
    }

    setRuleset(rulesetArrayBuffer) {
        this.sendCommand('SET_RULESET', { rulesetBuffer: rulesetArrayBuffer }, [rulesetArrayBuffer]);
    }
    resetWorld(initialState, seed) {
        this.lastResetSeed = Number.isFinite(seed) ? Number(seed) : null;
        this.tpsAggregator.ticksCounted = 0;
        this.tpsAggregator.startTime = performance.now();
        this.lastTickCountForServerUpdate = 0; 
        this.latestStats.tps = 0; 

        this.latestStats.ratioHistory = [];
        this.latestStats.entropyHistory = [];
        this.latestStats.hexBlockEntropyHistory = [];
        this.latestStats.ruleUsage.fill(0);

        this.sendCommand('RESET_WORLD', { initialState, seed });
    }
    // Run an auto-explore evaluation burst on this world's current (ruleset × state). Resolves with
    // the EVALUATION_RESULT summary. opts: { ticks, sampleEvery, warmupTicks, probe: { enabled,
    // flipIndex, probeTicks } }. `warmupTicks` (default 0) discards an early transient before metric
    // accumulation. The result adds `blockEntropy.spatialVariance`, `spatialOrder: {mean, last}`,
    // and `changeOrder: {mean, last}`.
    // A burst already in flight is superseded — its promise resolves with the new result rather than
    // hanging (the worker only ever emits one result per active burst).
    runEvaluation(opts = {}) {
        return new Promise((resolve) => {
            this._pendingEvaluation = resolve;
            this.sendCommand('RUN_EVALUATION', opts);
        });
    }
    // Capture an exact, bit-packed trajectory from the current state. The worker restores its Wasm
    // buffers and cached observables before replying, so the visible/live world does not advance.
    // sourceTick is sampled in the worker at the same instant as frame 0 (main-thread stats can lag).
    captureTrajectory({ frameCount = 32, tickStride = 8 } = {}) {
        return new Promise((resolve, reject) => {
            if (this._pendingTrajectoryCapture) {
                this._pendingTrajectoryCapture.reject(new Error('Trajectory capture was superseded.'));
            }
            this._pendingTrajectoryCapture = { resolve, reject };
            this.sendCommand('CAPTURE_TRAJECTORY', { frameCount, tickStride });
        });
    }
    // Capture consecutive non-overlapping slices in one non-destructive worker probe. Each next
    // slice starts frameCount * tickStride ticks after the previous slice's frame 0. Corpus Lab's
    // capture-at-judgment path is the only caller; the shipped single-slice UI uses captureTrajectory.
    captureTrajectorySeries({ frameCount = 32, tickStride = 1, sliceCount = 8 } = {}) {
        return new Promise((resolve, reject) => {
            if (this._pendingTrajectoryCapture) {
                this._pendingTrajectoryCapture.reject(new Error('Trajectory capture was superseded.'));
            }
            this._pendingTrajectoryCapture = { resolve, reject };
            this.sendCommand('CAPTURE_TRAJECTORY', { frameCount, tickStride, sliceCount });
        });
    }
    // Fetch the worker's detected-cycle frames (bit-packed state + rule indices per frame) for the
    // perfect-loop GIF export. Resolves null when no cycle is locked in. Single-pending contract
    // like runEvaluation: a newer request supersedes an older unresolved one.
    getCycleFrames() {
        return new Promise((resolve) => {
            this._pendingCycleFrames = resolve;
            this.sendCommand('GET_CYCLE_FRAMES', {});
        });
    }
    // Arm a perfect-run recording (frame 0 = current state). Resolves when the recording finishes —
    // the sim reaching a cycle, hitting `maxFrames`, a manual stopRunRecording(), or a discontinuity
    // (resolves `{cancelled:true}`). `onProgress(frames)` fires periodically while frames accumulate.
    startRunRecording(maxFrames, onProgress) {
        return new Promise((resolve) => {
            this._pendingRunRecording = resolve;
            this._runRecordingProgress = onProgress || null;
            this.sendCommand('START_RUN_RECORDING', { maxFrames });
        });
    }
    // Finish an armed run recording early with whatever's banked (worker replies RUN_RECORDING_COMPLETE).
    stopRunRecording() { this.sendCommand('STOP_RUN_RECORDING', {}); }
    // --- State-history scrub-back (selected world only) ---------------------
    // Enable/disable recording of recent state frames. The main thread captures only on the selected
    // world so the ring's memory is bounded to one world.
    setHistoryCapture(enabled) { this.sendCommand('SET_HISTORY_CAPTURE', { enabled }); }
    // Park on the frame `offset` ticks back from the live tip (0 = present). Caller pauses first.
    scrubHistory(offset) { this.sendCommand('STATE_HISTORY_SCRUB', { offset }); }
    // Leave scrub mode, discarding the recorded future beyond the scrub point.
    resumeHistory() { this.sendCommand('STATE_HISTORY_RESUME', {}); }
    // Advance the live sim exactly one tick while paused (forward step past the recorded tip).
    stepHistoryLive() { this.sendCommand('STATE_HISTORY_STEP_LIVE', {}); }
    // --- Spacetime volume stream (#40, selected world + open view only) -----
    // Arm/disarm the per-tick layer stream. Enabling also makes the worker ship the frames the scrub
    // ring already holds, so the object appears grown instead of building up over 240 ticks.
    setSpacetimeCapture(enabled) { this.sendCommand('SET_SPACETIME_CAPTURE', { enabled }); }
    // Hand a consumed spacetime layer buffer back for reuse (same ping-pong as STATE_UPDATE's).
    _reclaimBuffer(buffer) {
        if (!buffer || buffer.byteLength === 0) return;
        this.worker.postMessage({ type: 'RECLAIM_BUFFERS', data: { buffers: [buffer] } }, [buffer]);
    }
    applyBrush(col, row, brushSize) {
        this.sendCommand('APPLY_BRUSH', { col, row, brushSize });
    }
    applySelectiveBrush(cellIndices, brushMode = 'invert') {
        this.sendCommand('APPLY_SELECTIVE_BRUSH', { cellIndices, brushMode });
    }
    // Toroidally shift the cell state (and rule-index colouring) by whole cells, wrapping at the
    // edges. Used to re-centre a pattern that has drifted across the wrap seam. dCol should be even
    // so the odd-q column-stagger phase is preserved (an odd column shift would shear the pattern).
    shiftState(dCol, dRow) {
        this.sendCommand('SHIFT_STATE', { dCol, dRow });
    }
    // Hover is purely visual, so it's computed and stored main-thread-only (like the ghost preview)
    // instead of round-tripping the worker. Writes the per-cell highlight buffer directly and marks
    // the FBO dirty so the renderer redraws on the next frame.
    setHoverState(hoverAffectedIndices) {
        this.latestHoverStateArray.fill(0);
        for (const index of hoverAffectedIndices) {
            if (index >= 0 && index < this.latestHoverStateArray.length) {
                this.latestHoverStateArray[index] = 1;
            }
        }
        this.renderDirty = true;
    }
    clearHoverState() {
        this.latestHoverStateArray.fill(0);
        this.renderDirty = true;
    }

    getLatestRenderData() {
        return {
            jsStateArray: this.latestStateArray,
            jsRuleIndexArray: this.latestRuleIndexArray,
            jsHoverStateArray: this.latestHoverStateArray,
            jsGhostStateArray: this.latestGhostStateArray,
            enabled: this.latestStats.isEnabled,
            dirty: this.renderDirty,
        };
    }

    clearRenderDirty() {
        this.renderDirty = false;
    }

    markRenderDirty() {
        this.renderDirty = true;
    }

    getLatestStats() {
        
        return { ...this.latestStats };
    }

    getFullStatus() {
        return {
            renderData: this.getLatestRenderData(),
            stats: this.getLatestStats()
        };
    }

    terminate() {
        this.worker.terminate();
    }
}
